import {
  WATCHED_COUNTIES,
  countyReportPath,
  weekdayName,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { navigate, useAsync } from "../../lib/hooks";
import {
  draftBatch,
  fetchStormConfig,
  fetchStormEvents,
  pollStorms,
  saveStormThreshold,
  updateStormEvent,
  type StormEvent,
} from "../../lib/partnerQueries";

/**
 * What the weather did, and what to do about it.
 *
 * Two jobs on one screen, both Jonathon's:
 *
 *   * **Look at what the poller found.** Including the days it decided were
 *     unremarkable, because a season of "that was worth a text and we missed
 *     it" is how the thresholds get to be right.
 *   * **Overrule it.** A batch can be drafted for any day, and the summary
 *     sentence — which is the first line of forty text messages — can be
 *     rewritten before anybody sees it. The poller knows the numbers; it does
 *     not know where Pelham is.
 */

const number = (value: string | number | null): number | null => {
  if (value === null) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function StormsAdmin() {
  const eventsQuery = useAsync(fetchStormEvents, []);
  const configQuery = useAsync(fetchStormConfig, []);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  /*
   * What the last look could not reach.
   *
   * Three services, and one of them being down should not mean the other two
   * are ignored — so a poll steps over a failure and reports it here rather
   * than throwing. Shown, because "no storms found" and "we could not ask"
   * look identical on this screen and are not the same thing at all.
   */
  const [reachErrors, setReachErrors] = useState<string[]>([]);

  async function poll(): Promise<void> {
    setPolling(true);
    setError(null);
    try {
      const result = await pollStorms();
      setReachErrors(result.errors ?? []);
      await eventsQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPolling(false);
    }
  }

  return (
    <>
      <div className="listhead">
        <h2>Storms</h2>
        <div className="lh-right">
          <button
            className="btn-ghost"
            onClick={() => setShowSettings((value) => !value)}
          >
            {showSettings ? "Hide thresholds" : "Thresholds"}
          </button>
          <button
            className="btn-primary"
            disabled={polling}
            onClick={() => void poll()}
          >
            {polling ? "Looking…" : "Check now"}
          </button>
        </div>
      </div>

      <ErrorNote error={error} />

      {reachErrors.length > 0 && (
        <div className="blocked-note">
          <b>Some of it could not be reached</b>
          {reachErrors.join(" ")}
        </div>
      )}

      <div className="voice-note">
        <b>Where this comes from</b>
        <p>
          National Weather Service alerts, Local Storm Reports through the Iowa
          Environmental Mesonet, and the rain gauge and anemometer at{" "}
          {WATCHED_COUNTIES.map((county) => county.asos).join(" and ")}. All
          free, all public, all checkable — which is what makes the county
          report worth forwarding.
        </p>
      </div>

      {showSettings && (
        <Thresholds
          rows={configQuery.data ?? []}
          onSaved={() => void configQuery.reload()}
        />
      )}

      <div className="list">
        <div className="lrow head">
          <div>County</div>
          <div>Day</div>
          <div>What happened</div>
          <div>Hail</div>
          <div>Wind</div>
          <div>Rain</div>
          <div>Batch</div>
        </div>

        {(eventsQuery.data ?? []).length === 0 ? (
          <div className="empty">
            <b>Nothing yet</b>
            The poller has not found a storm in either county. Press Check now
            to look.
          </div>
        ) : (
          (eventsQuery.data ?? []).map((event) => (
            <StormRow
              key={event.id}
              event={event}
              onChanged={() => void eventsQuery.reload()}
              onError={setError}
            />
          ))
        )}
      </div>
    </>
  );
}

function StormRow({
  event,
  onChanged,
  onError,
}: {
  event: StormEvent;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState(event.summary ?? "");
  const [busy, setBusy] = useState(false);

  async function draft(): Promise<void> {
    setBusy(true);
    try {
      await draftBatch("storm", event.id);
      onChanged();
      navigate("partners-queue");
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveSummary(): Promise<void> {
    setBusy(true);
    try {
      await updateStormEvent(event.id, { summary: summary.trim() || null });
      setEditing(false);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const county = WATCHED_COUNTIES.find((row) => row.key === event.county);
  const hail = number(event.max_hail_in);
  const wind = number(event.max_wind_mph);
  const rain = number(event.rain_in_24h);

  return (
    <div className="lrow storm">
      <div>
        <div className="cust">{county?.label ?? event.county}</div>
        {event.created_by_hand && <span className="sub">Added by hand</span>}
      </div>
      <div className="sub">
        {weekdayName(event.event_date)} {event.event_date}
      </div>
      <div>
        {event.triggers.length === 0 ? (
          <span className="sub">Nothing cleared a threshold</span>
        ) : (
          event.triggers.map((trigger) => (
            <span key={trigger} className="pill">
              {trigger.replace("_", " ")}
            </span>
          ))
        )}
        {editing ? (
          <div className="rowacts">
            <textarea
              rows={2}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
            <button
              className="btn-small"
              disabled={busy}
              onClick={() => void saveSummary()}
            >
              Save
            </button>
            <button className="btn-small" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <p className="sub">
            {event.summary ?? "No sentence written."}{" "}
            <button className="linky" onClick={() => setEditing(true)}>
              edit
            </button>
          </p>
        )}
      </div>
      <div className="sub">{hail === null ? "—" : `${hail.toFixed(2)}"`}</div>
      <div className="sub">
        {wind === null ? "—" : `${Math.round(wind)} mph`}
      </div>
      <div className="sub">{rain === null ? "—" : `${rain.toFixed(2)}"`}</div>
      <div className="rowacts">
        {event.batch_id ? (
          <button
            className="btn-small"
            onClick={() => navigate("partners-queue")}
          >
            In the queue
          </button>
        ) : (
          <button
            className="btn-small"
            disabled={busy}
            onClick={() => void draft()}
          >
            Draft a batch
          </button>
        )}
        <a
          className="btn-small"
          href={
            event.report_url ?? countyReportPath(event.county, event.event_date)
          }
          target="_blank"
          rel="noreferrer"
        >
          The report
        </a>
      </div>
    </div>
  );
}

/**
 * The thresholds, editable.
 *
 * In a table rather than in code so a season of near-misses can tune them from
 * here. Hail's is zero and is meant to stay zero: any hail report at all in
 * either county is worth a text, because hail is the peril that pays for a
 * roof.
 */
export function Thresholds({
  rows,
  onSaved,
}: {
  rows: { key: string; value: string | number; unit: string; label: string }[];
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function save(key: string): Promise<void> {
    // An empty box is not zero. `Number("")` is 0, which is finite and not
    // negative, so clearing the wind threshold and pressing Save used to write
    // 0 mph — a threshold every day on record exceeds, and forty people texted
    // about a breeze. Zero itself has to stay savable, because zero is hail's
    // real setting: any hail report at all is worth a text.
    const raw = (draft[key] ?? "").trim();
    const value = Number(raw);
    if (raw === "" || !Number.isFinite(value) || value < 0) {
      setError("That is not a number.");
      return;
    }
    setError(null);
    try {
      await saveStormThreshold(key, value);
      setDraft((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="thresholds">
      <ErrorNote error={error} />
      {rows.map((row) => {
        const shown = draft[row.key] ?? String(row.value);
        const dirty = draft[row.key] !== undefined;
        return (
          <div key={row.key} className="field">
            <label htmlFor={`th-${row.key}`}>{row.label}</label>
            <div className="rowacts">
              <input
                id={`th-${row.key}`}
                type="number"
                step="0.1"
                value={shown}
                onChange={(e) =>
                  setDraft((current) => ({
                    ...current,
                    [row.key]: e.target.value,
                  }))
                }
              />
              <span className="sub">{row.unit}</span>
              {dirty && (
                <button
                  className="btn-small"
                  onClick={() => void save(row.key)}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
